// @client/components/profile-manager/ProfileTable.tsx
// ✅ FINAL FIX: Categories now save correctly

import React, { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { ChevronDown, ChevronUp, Edit3, Check, X, Settings, ExternalLink } from "lucide-react";
import { Button } from "@client/components/ui/button";
import { Input } from "@client/components/ui/input";
import { Badge } from "@client/components/ui/badge";
import { Checkbox } from "@client/components/ui/checkbox";
import { Avatar, AvatarFallback, AvatarImage } from "@client/components/ui/avatar";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@client/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuCheckboxItem
} from "@client/components/ui/dropdown-menu";
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from "@client/components/ui/tooltip";

import { CategoryPills, ListPills } from "./RelationsPills";
import { InlineCategoryEditor } from "./InlineCategoryEditor";
import { QuickEditBadge } from "./QuickEditPopover";
import { useProfileUpdateMutation, useDataValuesQuery } from "@client/hooks/useProfilesQuery";

import type { 
  ProfileWithRelations,
  Category,
  List,
  ColumnVisibility,
  SortingState
} from "@shared/types";

export interface ProfileTableProps {
  profiles: ProfileWithRelations[];
  selectedProfiles: number[];
  columnVisibility: ColumnVisibility;
  sorting: SortingState;
  categories: Category[];
  lists: List[];
  onProfileSelection: (profileId: number) => void;
  onSelectAll: () => void;
  onSortingChange: (field: string, order?: 'asc' | 'desc') => void;
  onColumnVisibilityChange: (visibility: ColumnVisibility) => void;
  onEditProfile?: (profile: ProfileWithRelations) => void;
  onQuickEdit: (profileId: number, field: string, value: any) => void;
  editingCategory: number | null;
  onEditCategory: (profileId: number | null) => void;
  loading?: boolean; 
}

export function ProfileTable({
  profiles,
  selectedProfiles,
  columnVisibility,
  sorting,
  categories,
  lists,
  onProfileSelection,
  onSelectAll,
  onSortingChange,
  onColumnVisibilityChange,
  onEditProfile,
  onQuickEdit,  // ✅ Use this for categories!
  loading
}: ProfileTableProps) {
  const [searchParams] = useSearchParams();

  const [editingScoreId, setEditingScoreId] = useState<number | null>(null);
  const [editingCategoryId, setEditingCategoryId] = useState<number | null>(null);
  const [scoreValue, setScoreValue] = useState<string>('');

  const profileUpdateMutation = useProfileUpdateMutation();
  const queryClient = useQueryClient(); 
  
  const { data: genderValues = [] } = useDataValuesQuery('gender');
  const { data: industryValues = [] } = useDataValuesQuery('industry');

  const isAllSelected = profiles.length > 0 && profiles.every(p => selectedProfiles.includes(p.id));

  const handleSortClick = (field: typeof sorting.field) => {
    const newOrder = sorting.field === field && sorting.order === 'asc' ? 'desc' : 'asc';
    onSortingChange(field, newOrder);
  };

  const handleStartScoreEdit = (profileId: number, currentScore: number | null) => {
    setEditingScoreId(profileId);
    setScoreValue((currentScore || 0).toString());
  };

  const handleScoreSubmit = async (profileId: number) => {
    const score = parseInt(scoreValue);
    if (isNaN(score) || score < 0 || score > 100) return;

    await profileUpdateMutation.mutateAsync({ profileId, updates: { score } });
    setEditingScoreId(null);
  };

  const handleScoreCancel = () => {
    setEditingScoreId(null);
    setScoreValue('');
  };

  const handleQuickEditField = async (profileId: number, field: string, value: string) => {
    await profileUpdateMutation.mutateAsync({ 
      profileId, 
      updates: { [field]: value } 
    });
  };

  // ✅ FIXED: Use onQuickEdit from parent (handles categories correctly)
  const handleCategorySave = async (profileId: number, categoryIds: number[]) => {
  console.log('[ProfileTable] Category save complete, triggering refetch');
  
  try {
    // ✅ Manually invalidate cache to force table reload
    await queryClient.invalidateQueries({ queryKey: ['profiles'] });
    console.log('[ProfileTable] Cache invalidated, table should reload');
  } catch (error) {
    console.error('[ProfileTable] Cache invalidation failed:', error);
  }
  
  // Close editor
  setEditingCategoryId(null);
};

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(word => word.charAt(0))
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const formatDate = (dateString: string | Date | null) => {
    if (!dateString) return '—';
    try {
      return new Date(dateString).toLocaleDateString('de-DE');
    } catch {
      return '—';
    }
  };

  const getScoreColor = (score: number | null) => {
    if (score === null) return '';
    if (score >= 80) return 'text-green-700 border-green-500';
    if (score >= 50) return 'text-yellow-700 border-yellow-500';
    return 'text-red-700 border-red-500';
  };

  return (
    <TooltipProvider>
      <div className="rounded-md border">
        <div className="flex items-center justify-between p-4 border-b">
          <div className="text-sm font-medium">
            {profiles.length} profile{profiles.length !== 1 ? 's' : ''} displayed
          </div>
          
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm">
                <Settings className="h-4 w-4 mr-2" />
                Columns
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuLabel>Toggle Columns</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {Object.entries(columnVisibility).map(([key, visible]) => (
                <DropdownMenuCheckboxItem
                  key={key}
                  checked={visible}
                  onCheckedChange={(checked) => 
                    onColumnVisibilityChange({
                      ...columnVisibility,
                      [key]: checked
                    })
                  }
                >
                  {key.charAt(0).toUpperCase() + key.slice(1)}
                </DropdownMenuCheckboxItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-12">
                <Checkbox 
                  checked={isAllSelected}
                  onCheckedChange={onSelectAll}
                  aria-label="Select all profiles"
                />
              </TableHead>

              <TableHead className="cursor-pointer" onClick={() => handleSortClick('name')}>
                <div className="flex items-center">
                  Profile
                  {sorting.field === 'name' && (
                    sorting.order === 'asc' ? <ChevronUp className="h-4 w-4 ml-1" /> : <ChevronDown className="h-4 w-4 ml-1" />
                  )}
                </div>
              </TableHead>

              {columnVisibility.categories && <TableHead>Categories</TableHead>}
              {columnVisibility.lists && <TableHead>Lists</TableHead>}
              {columnVisibility.score && (
                <TableHead className="cursor-pointer" onClick={() => handleSortClick('score')}>
                  <div className="flex items-center">
                    Score
                    {sorting.field === 'score' && (
                      sorting.order === 'asc' ? <ChevronUp className="h-4 w-4 ml-1" /> : <ChevronDown className="h-4 w-4 ml-1" />
                    )}
                  </div>
                </TableHead>
              )}
              {columnVisibility.gender && <TableHead>Gender</TableHead>}
              {columnVisibility.industry && <TableHead>Industry</TableHead>}
              {columnVisibility.company && (
                <TableHead className="cursor-pointer" onClick={() => handleSortClick('company')}>
                  <div className="flex items-center">
                    Company
                    {sorting.field === 'company' && (
                      sorting.order === 'asc' ? <ChevronUp className="h-4 w-4 ml-1" /> : <ChevronDown className="h-4 w-4 ml-1" />
                    )}
                  </div>
                </TableHead>
              )}
              {columnVisibility.location && (
                <TableHead className="cursor-pointer" onClick={() => handleSortClick('location')}>
                  <div className="flex items-center">
                    Location
                    {sorting.field === 'location' && (
                      sorting.order === 'asc' ? <ChevronUp className="h-4 w-4 ml-1" /> : <ChevronDown className="h-4 w-4 ml-1" />
                    )}
                  </div>
                </TableHead>
              )}
              {columnVisibility.created && (
                <TableHead className="cursor-pointer" onClick={() => handleSortClick('createdAt')}>
                  <div className="flex items-center">
                    Created
                    {sorting.field === 'createdAt' && (
                      sorting.order === 'asc' ? <ChevronUp className="h-4 w-4 ml-1" /> : <ChevronDown className="h-4 w-4 ml-1" />
                    )}
                  </div>
                </TableHead>
              )}
              {columnVisibility.exported && <TableHead>Exported</TableHead>}
              {columnVisibility.linkedinId && <TableHead>LinkedIn</TableHead>}
              {columnVisibility.status && <TableHead>Status</TableHead>}
              <TableHead className="w-20">Actions</TableHead>
            </TableRow>
          </TableHeader>
          
          <TableBody>
            {profiles.map((profile) => (
              <TableRow key={profile.id} className="group">
                <TableCell>
                  <Checkbox 
                    checked={selectedProfiles.includes(profile.id)}
                    onCheckedChange={() => onProfileSelection(profile.id)}
                    aria-label={`Select profile ${profile.name}`}
                  />
                </TableCell>
                
                <TableCell>
                  <div className="flex items-center gap-3">
                    <Avatar className="h-8 w-8">
                      <AvatarImage src={profile.imageUrl || undefined} />
                      <AvatarFallback className="text-xs">
                        {getInitials(profile.name || 'UN')}
                      </AvatarFallback>
                    </Avatar>
                    <div className="space-y-1">
                      <div className="font-medium text-sm">
                        <Link 
                          to={`/profile-manager/${profile.id}${
                            searchParams.toString() 
                            ? `?${searchParams.toString()}` 
                            : ''
                            }`}
                          className="text-blue-600 hover:text-blue-800 hover:underline"
                        >
                          {profile.name}
                        </Link>
                      </div>
                      {profile.title && (
                        <div className="text-sm text-muted-foreground">{profile.title}</div>
                      )}
                    </div>
                    {onEditProfile && (
                      <Button 
                        size="sm" 
                        variant="ghost" 
                        onClick={() => onEditProfile(profile)}
                        className="opacity-0 group-hover:opacity-100 transition-opacity h-8 w-8 p-0"
                        title={`Edit ${profile.name}`}
                      >
                        <Edit3 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </TableCell>
                
                {columnVisibility.categories && (
                  <TableCell>
                    {editingCategoryId === profile.id ? (
                      <InlineCategoryEditor
                        profileId={profile.id}
                        currentCategories={profile.categories}
                        allCategories={categories}
                        onSave={handleCategorySave}
                        onCancel={() => setEditingCategoryId(null)}
                        showColors={true}
                        isOpen={true}
                        onOpenChange={(open) => !open && setEditingCategoryId(null)}
                      />
                    ) : (
                      <CategoryPills 
                        categories={profile.categories || []}
                        onEdit={() => setEditingCategoryId(profile.id)}
                      />
                    )}
                  </TableCell>
                )}

                {columnVisibility.lists && (
                  <TableCell>
                    <ListPills 
                      lists={profile.lists || []}
                    />
                  </TableCell>
                )}

                {columnVisibility.score && (
                  <TableCell>
                    {editingScoreId === profile.id ? (
                      <div className="flex items-center gap-2">
                        <Input
                          type="number"
                          min="0"
                          max="100"
                          value={scoreValue}
                          onChange={(e) => setScoreValue(e.target.value)}
                          className="w-20"
                          autoFocus
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') handleScoreSubmit(profile.id);
                            if (e.key === 'Escape') handleScoreCancel();
                          }}
                        />
                        <Button 
                          size="sm" 
                          onClick={() => handleScoreSubmit(profile.id)}
                          disabled={profileUpdateMutation.isPending}
                        >
                          <Check className="h-4 w-4" />
                        </Button>
                        <Button 
                          size="sm" 
                          variant="ghost" 
                          onClick={handleScoreCancel}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <Badge 
                          variant="outline" 
                          className={getScoreColor(profile.score)}
                        >
                          {profile.score !== null ? profile.score : '—'}
                        </Badge>
                        <Button 
                          size="sm" 
                          variant="ghost" 
                          onClick={() => handleStartScoreEdit(profile.id, profile.score)}
                          className="opacity-0 group-hover:opacity-100 transition-opacity h-6 w-6 p-0"
                        >
                          <Edit3 className="h-3 w-3" />
                        </Button>
                      </div>
                    )}
                  </TableCell>
                )}

                {columnVisibility.gender && (
                  <TableCell>
                    <QuickEditBadge
                      value={profile.gender}
                      options={genderValues}
                      onSave={(value) => handleQuickEditField(profile.id, 'gender', value)}
                      label="Gender"
                      emptyText="Set"
                      variant="outline"
                    />
                  </TableCell>
                )}

                {columnVisibility.industry && (
                  <TableCell>
                    <QuickEditBadge
                      value={profile.industry}
                      options={industryValues}
                      onSave={(value) => handleQuickEditField(profile.id, 'industry', value)}
                      label="Industry"
                      emptyText="Set"
                      variant="secondary"
                    />
                  </TableCell>
                )}

                {columnVisibility.company && (
                  <TableCell>
                    <div className="text-sm">{profile.company || '—'}</div>
                  </TableCell>
                )}

                {columnVisibility.location && (
                  <TableCell>
                    <div className="text-sm">{profile.location || '—'}</div>
                  </TableCell>
                )}

                {columnVisibility.created && (
                  <TableCell>
                    <div className="text-sm">{formatDate(profile.createdAt)}</div>
                  </TableCell>
                )}

                {columnVisibility.exported && (
                  <TableCell>
                    <div className="text-sm">{formatDate(profile.exportedAt)}</div>
                  </TableCell>
                )}

                {columnVisibility.linkedinId && (
                  <TableCell>
                    <div className="text-xs font-mono text-muted-foreground">
                      {profile.linkedinId || '—'}
                    </div>
                  </TableCell>
                )}

                {columnVisibility.status && (
                  <TableCell>
                    <div className="flex gap-1">
                      {profile.isActive && (
                        <Badge variant="outline" className="text-xs">Active</Badge>
                      )}
                      {profile.isBlocked && (
                        <Badge variant="destructive" className="text-xs">Blocked</Badge>
                      )}
                      {profile.isRegistered && (
                        <Badge variant="secondary" className="text-xs">Registered</Badge>
                      )}
                    </div>
                  </TableCell>
                )}
                
                <TableCell>
                  <div className="flex items-center gap-1">
                    {profile.profileUrl && (
                      <Button variant="ghost" size="sm" asChild>
                        <a 
                          href={profile.profileUrl} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          title="Open LinkedIn Profile"
                        >
                          <ExternalLink className="h-4 w-4" />
                        </a>
                      </Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        {loading && (
          <div className="text-center py-8 text-muted-foreground">
            Loading profiles...
          </div>
        )}
      </div>
    </TooltipProvider>
  );
}